import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { listConversations, type Conversation } from "../lib/messaging";
import { initials } from "../profiles/ProfileMenu";
import { Icon } from "../app/Icon";

function ago(at?: string): string {
  if (!at) return "";
  const s = Math.max(0, (Date.now() - +new Date(at)) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** Two-pane messenger: conversation list (left) + active thread (right, Outlet). */
export function MessagesLayout() {
  const { pathname } = useLocation();
  const hasThread = pathname !== "/messages";
  const activeId = hasThread ? pathname.split("/").pop() : null;
  const [items, setItems] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listConversations()
      .then((r) => setItems(r.conversations))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="grid h-[calc(100dvh-8.5rem)] overflow-hidden rounded-card border border-line bg-surface lg:grid-cols-[320px_1fr]">
      <aside className={`${hasThread ? "hidden lg:flex" : "flex"} min-h-0 flex-col border-line lg:border-r`}>
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="font-mono text-xs font-bold uppercase tracking-widest text-muted">Conversations</span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <p className="p-4 font-mono text-sm text-muted">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-4 font-mono text-sm text-muted">No conversations yet. Message someone from their profile.</p>
          ) : (
            <ul>
              {items.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/messages/${c.id}`}
                    className={`flex items-center gap-3 border-l-2 px-4 py-3 transition-colors ${activeId === c.id ? "border-accent bg-surface-2" : "border-transparent hover:bg-surface-2"}`}
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent/15 font-display text-xs font-black text-accent-strong">{initials(c.other_name)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-bold">{c.other_name}</span>
                        <span className="shrink-0 font-mono text-[10px] text-muted">{ago(c.last_message_at)}</span>
                      </span>
                      {c.last_message && <span className="block truncate text-xs text-muted">{c.last_message}</span>}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      <section className={`${hasThread ? "flex" : "hidden lg:flex"} min-h-0 flex-col`}>
        <Outlet context={{ conversations: items }} />
      </section>
    </div>
  );
}

export function MessagesEmpty() {
  return (
    <div className="grid flex-1 place-items-center p-10 text-center">
      <div className="grid justify-items-center gap-2">
        <Icon name="messages" className="h-8 w-8 text-muted" />
        <p className="font-mono text-sm text-muted">Select a conversation to start messaging.</p>
      </div>
    </div>
  );
}
